import { useState } from "react";
import { BellRing, Boxes, Building2, ChevronRight, CircleDollarSign, ClipboardList, FileImage, Gauge, Gift, LayoutPanelTop, LogOut, Menu, PackageSearch, Settings, ShieldCheck, ShoppingBag, Tags, UserCog, UsersRound, Volume2, VolumeX, Warehouse, WifiOff, X } from "lucide-react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { OrderAlertProvider, useOrderAlert } from "../context/OrderAlertContext";
import { useSession } from "../context/SessionContext";
import type { SessionUser } from "../lib/types";
import { Button, Skeleton } from "./ui";

const adminNav = [
  ["/admin", "Dashboard", Gauge],
  ["/admin/pesanan", "Pesanan", ClipboardList],
  ["/admin/produk", "Produk", ShoppingBag],
  ["/admin/stok", "Stok", Warehouse],
  ["/admin/pembayaran", "Pembayaran", CircleDollarSign],
  ["/admin/media", "Katalog & Media", FileImage],
  ["/admin/pelanggan", "Pelanggan", UsersRound],
  ["/admin/member", "Member & Poin", Gift],
  ["/admin/pengaturan", "Pengaturan", Settings],
] as const;

const ownerNav = [
  ["/owner", "Dashboard", Gauge],
  ["/owner/cabang", "Cabang", Building2],
  ["/owner/admin", "Admin & Akses", UserCog],
  ["/owner/pesanan", "Pesanan", ClipboardList],
  ["/owner/produk", "Produk", ShoppingBag],
  ["/owner/kategori", "Kategori", Tags],
  ["/owner/stok", "Stok", Warehouse],
  ["/owner/pembayaran", "Metode Pembayaran", CircleDollarSign],
  ["/owner/pelanggan", "Pelanggan", UsersRound],
  ["/owner/member", "Member & Poin", Gift],
  ["/owner/tampilan", "Tampilan Website", LayoutPanelTop],
  ["/owner/media", "Media", FileImage],
  ["/owner/laporan", "Laporan", Boxes],
  ["/owner/pengaturan", "WhatsApp & Pengaturan", Settings],
  ["/owner/keamanan", "Password Owner", ShieldCheck],
] as const;

export function ProtectedRoute({ role }: { role: SessionUser["role"] }) {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) return <main className="page-shell inner-page"><Skeleton rows={6} /></main>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.role !== role) return <Navigate to={user.role === "OWNER" ? "/owner" : "/admin"} replace />;
  return <Outlet />;
}

export function DashboardLayout({ role }: { role: SessionUser["role"] }) {
  return (
    <OrderAlertProvider>
      <DashboardLayoutContent role={role} />
    </OrderAlertProvider>
  );
}

function DashboardLayoutContent({ role }: { role: SessionUser["role"] }) {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const {
    isLive,
    muted,
    setMuted,
    soundBlocked,
    activateSound,
    unconfirmedTotal,
    activeAlarmBranches,
  } = useOrderAlert();
  const nav = role === "OWNER" ? ownerNav : adminNav;
  const branchLocked = role === "ADMIN";
  const performLogout = async () => {
    await logout();
    void navigate("/login", { replace: true });
  };
  return (
    <div className="dashboard-layout">
      <aside className={`dashboard-sidebar ${open ? "dashboard-sidebar--open" : ""}`}>
        <div className="dashboard-sidebar__brand"><a className="brand brand--inverse brand--dashboard" href="/" aria-label="Warung Sedap Sayur, buka toko"><img className="brand__logo" src="/brand/warung-sedap-sayur-logo.webp" width={1190} height={433} alt="" aria-hidden="true" /><small>{role === "OWNER" ? "Ruang Owner" : "Ruang Admin"}</small></a><button type="button" onClick={() => setOpen(false)} aria-label="Tutup menu"><X aria-hidden="true" /></button></div>
        {branchLocked && <div className="branch-lock"><span>Admin Cabang</span><strong>{user?.branchName ?? "Cabang Anda"} 🔒</strong><small>Terkunci ke cabang ini</small></div>}
        <section className={`sidebar-alarm ${isLive ? "sidebar-alarm--live" : "sidebar-alarm--offline"}`} aria-label="Status alarm pesanan">
          <div className="sidebar-alarm__status" aria-live="polite">
            {isLive ? <BellRing aria-hidden="true" /> : <WifiOff aria-hidden="true" />}
            <span>
              <strong>{isLive ? "LIVE" : "OFFLINE"}</strong>
               <small>
                 {isLive
                   ? "Internet & notifikasi tersambung"
                   : "Menyambungkan ulang notifikasi"}
               </small>
            </span>
          </div>
          <p>
            <strong>{unconfirmedTotal}</strong> pesanan belum dikonfirmasi
            {role === "OWNER" ? ` · ${activeAlarmBranches} alarm cabang aktif` : ""}.
          </p>
          <small>
            Suara berhenti setelah semua pesanan dikonfirmasi atau saat suara dimatikan.
          </small>
          <button
            type="button"
            className="sidebar-alarm__sound"
            onClick={() =>
              soundBlocked || muted ? activateSound() : setMuted(true)
            }
          >
            {soundBlocked || muted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
            {soundBlocked
              ? "Izinkan suara"
              : muted
                ? "Aktifkan suara"
                : "Matikan suara"}
          </button>
        </section>
        <nav aria-label={`Menu ${role === "OWNER" ? "Owner" : "Admin"}`}>{nav.map(([to, label, Icon]) => <NavLink key={to} to={to} end={to === `/${role.toLowerCase()}`} onClick={() => setOpen(false)}><Icon aria-hidden="true" /><span>{label}</span><ChevronRight aria-hidden="true" className="nav-chevron" /></NavLink>)}</nav>
        <Button variant="quiet" onClick={() => void performLogout()} icon={<LogOut aria-hidden="true" />}>Keluar</Button>
      </aside>
      {open && <button className="sidebar-backdrop" type="button" onClick={() => setOpen(false)} aria-label="Tutup menu" />}
      <div className="dashboard-main">
        <header className="dashboard-topbar">
          <button type="button" className="icon-button" onClick={() => setOpen(true)} aria-label="Buka menu"><Menu aria-hidden="true" /></button>
          <div className={`dashboard-live-pill ${isLive ? "dashboard-live-pill--live" : "dashboard-live-pill--offline"}`} role="status" aria-live="polite">
            <span aria-hidden="true" />
            <strong>{isLive ? "LIVE" : "OFFLINE"}</strong>
          </div>
          <div><small>{role === "OWNER" ? "Owner Warung Sedap Sayur" : "Admin Cabang"}</small><strong>{user?.name}</strong></div>
          <a className="button button--secondary" href="/" target="_blank"><PackageSearch aria-hidden="true" /><span>Lihat Toko</span></a>
        </header>
        <main id="main-content" className="dashboard-content"><Outlet /></main>
      </div>
    </div>
  );
}
